/**
 * Calls > Automation panel.
 *
 * Alarm-style scheduler for outbound reminder calls. Lists scheduled calls and
 * lets the owner create one (once / daily / weekly), toggle it on-off, or cancel.
 *
 * Backend contract (hub-people handlers/scheduled_calls.rs):
 *   GET  /api/call-flows                     -> CallFlow[]
 *   POST /api/call-flows        {name,script?}-> CallFlow   (empty script => default voice reminder flow)
 *   GET  /api/scheduled-calls                -> ScheduledCall[]
 *   POST /api/scheduled-calls   {...}         -> ScheduledCall
 *   POST /api/scheduled-calls/:id/toggle {enabled} -> ScheduledCall
 *   POST /api/scheduled-calls/:id/cancel     -> ScheduledCall
 *
 * Recurrence fields (recurrence_kind / recurrence_days / recurrence_time_local /
 * enabled) are part of the same contract. We always also send a concrete future
 * `scheduled_for_utc` so creation works whether or not the server derives it.
 */

import { get, post } from '../api';

type RepeatKind = 'none' | 'daily' | 'weekly';

interface CallFlow {
  id: string;
  name: string;
  active: boolean;
}

interface ScheduledCall {
  id: string;
  flow_id: string;
  recipient_phone: string;
  scheduled_for_utc: string;
  original_time_input: string;
  timezone: string;
  status: string;
  next_attempt_at: string | null;
  last_error: string | null;
  cancelled_at: string | null;
  recurrence_kind?: RepeatKind;
  recurrence_days?: number[];
  recurrence_time_local?: string;
  enabled?: boolean;
}

const DEFAULT_RECIPIENT = '+61498108142';
const DEFAULT_FLOW_NAME = 'Appointment reminder';
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

let scheduled: ScheduledCall[] = [];
let flows: CallFlow[] = [];
let showForm = false;

// Draft state for the create form.
interface Draft {
  recipient: string;
  repeat: RepeatKind;
  time: string;          // HH:MM (24h, local)
  date: string;          // YYYY-MM-DD (one-time only)
  days: Set<number>;     // 0..6 (weekly)
}
let draft: Draft = freshDraft();

function freshDraft(): Draft {
  const now = new Date();
  const inOneHour = new Date(now.getTime() + 60 * 60 * 1000);
  return {
    recipient: DEFAULT_RECIPIENT,
    repeat: 'none',
    time: hhmm(inOneHour),
    date: ymd(inOneHour),
    days: new Set<number>([inOneHour.getDay()]),
  };
}

function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}
function hhmm(d: Date): string {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
function ymd(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function browserTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Australia/Sydney';
  } catch {
    return 'Australia/Sydney';
  }
}

/** Pretty 12h time from "HH:MM". */
function prettyClock(time: string): string {
  const [hStr, mStr] = time.split(':');
  const h = Number(hStr);
  const m = Number(mStr);
  if (Number.isNaN(h) || Number.isNaN(m)) return time;
  const mer = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${pad2(m)} ${mer}`;
}

/** Compute the next future local occurrence as a Date. */
function computeNext(repeat: RepeatKind, time: string, date: string, days: Set<number>): Date {
  const parts = time.split(':');
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (repeat === 'none') {
    return new Date(`${date}T${time}:00`);
  }
  const now = new Date();
  const candidate = new Date();
  candidate.setSeconds(0, 0);
  candidate.setHours(h, m, 0, 0);
  if (repeat === 'daily') {
    if (candidate <= now) candidate.setDate(candidate.getDate() + 1);
    return candidate;
  }
  // weekly: find the soonest selected weekday at the given time.
  const wanted = days.size > 0 ? days : new Set<number>([now.getDay()]);
  for (let i = 0; i < 8; i++) {
    const c = new Date(now);
    c.setDate(now.getDate() + i);
    c.setHours(h, m, 0, 0);
    if (wanted.has(c.getDay()) && c > now) return c;
  }
  candidate.setDate(candidate.getDate() + 1);
  return candidate;
}

function repeatLabel(c: ScheduledCall): string {
  const kind = c.recurrence_kind ?? 'none';
  if (kind === 'daily') return 'Every day';
  if (kind === 'weekly') {
    const ds = (c.recurrence_days ?? []).slice().sort((a, b) => a - b);
    if (ds.length === 7) return 'Every day';
    if (ds.length === 0) return 'Weekly';
    return ds.map(d => DAY_LABELS[d] ?? '').join(', ');
  }
  // one-time
  const when = c.scheduled_for_utc ? new Date(c.scheduled_for_utc) : null;
  return when ? `Once · ${when.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}` : 'Once';
}

function alarmTime(c: ScheduledCall): string {
  if (c.recurrence_time_local) return prettyClock(c.recurrence_time_local);
  const when = c.scheduled_for_utc ? new Date(c.scheduled_for_utc) : null;
  return when ? when.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '--:--';
}

function statusBadge(c: ScheduledCall): string {
  const enabled = c.enabled !== false;
  const s = c.status;
  let label = s;
  let color = 'var(--fg-muted)';
  if (!enabled) { label = 'Off'; color = 'var(--fg-muted)'; }
  else if (s === 'pending') { label = 'Scheduled'; color = 'var(--accent)'; }
  else if (s === 'dialing' || s === 'claimed') { label = 'Calling'; color = 'var(--blue, #2563eb)'; }
  else if (s === 'done') { label = 'Done'; color = 'var(--fg-muted)'; }
  else if (s === 'suppressed') { label = 'Paused 24h'; color = 'var(--orange, #ea580c)'; }
  else if (s === 'failed') { label = 'Failed'; color = 'var(--danger, #dc2626)'; }
  else if (s === 'cancelled') { label = 'Cancelled'; color = 'var(--fg-muted)'; }
  return `<span style="font-size:11px;font-weight:600;color:${color}">${esc(label)}</span>`;
}

async function loadData(): Promise<void> {
  const [sc, fl] = await Promise.all([
    get<ScheduledCall[]>('/api/scheduled-calls').catch(() => [] as ScheduledCall[]),
    get<CallFlow[]>('/api/call-flows').catch(() => [] as CallFlow[]),
  ]);
  scheduled = Array.isArray(sc) ? sc : [];
  flows = Array.isArray(fl) ? fl : [];
}

/** Ensure an active reminder flow exists; create the default one if not. */
async function ensureFlowId(): Promise<string> {
  const active = flows.find(f => f.active) ?? flows[0];
  if (active) return active.id;
  const created = await post<CallFlow>('/api/call-flows', { name: DEFAULT_FLOW_NAME, script: '' });
  flows = [created, ...flows];
  return created.id;
}

export async function renderAutomation(host: HTMLElement, _serverId: string): Promise<void> {
  host.innerHTML = `<div style="padding:24px;color:var(--fg-muted);font-family:var(--font-sans)">Loading automations…</div>`;
  await loadData();
  paint(host);
}

function paint(host: HTMLElement): void {
  host.innerHTML = `
    <div style="flex:1;min-height:0;display:flex;flex-direction:column;font-family:var(--font-sans)">
      <div style="padding:10px 16px;border-bottom:1px solid var(--border);display:flex;align-items:center;gap:10px;font-size:13px">
        <div style="font-weight:600;color:var(--fg)">Scheduled calls</div>
        <div style="color:var(--fg-muted)">${scheduled.length} ${scheduled.length === 1 ? 'alarm' : 'alarms'}</div>
        <button id="auto-new" style="margin-left:auto;background:var(--accent, #16a34a);border:none;color:var(--fg-on-accent, #fff);padding:6px 14px;border-radius:6px;cursor:pointer;font-size:13px;font-weight:600;display:inline-flex;gap:6px;align-items:center">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="var(--fg-on-accent, #fff)"><path d="M11 5h2v14h-2z"/><path d="M5 11h14v2H5z"/></svg>
          New
        </button>
      </div>
      <div id="auto-body" style="flex:1;min-height:0;overflow-y:auto"></div>
    </div>
  `;
  host.querySelector<HTMLButtonElement>('#auto-new')?.addEventListener('click', () => {
    draft = freshDraft();
    showForm = true;
    paintBody(host);
  });
  paintBody(host);
}

function paintBody(host: HTMLElement): void {
  const body = host.querySelector<HTMLElement>('#auto-body');
  if (!body) return;
  body.innerHTML = (showForm ? formHtml() : '') + listHtml();
  if (showForm) wireForm(host);
  wireList(host);
}

function listHtml(): string {
  const active = scheduled.filter(c => c.status !== 'cancelled');
  if (active.length === 0 && !showForm) {
    return `
      <div style="padding:48px 20px;text-align:center;color:var(--fg-muted)">
        <div style="font-size:18px;margin-bottom:8px">No scheduled calls yet</div>
        <div style="font-size:13px">Tap <strong style="color:var(--fg)">New</strong> to set a reminder call — like an alarm that rings your phone and asks if you've left.</div>
      </div>
    `;
  }
  return active.map(rowHtml).join('');
}

function rowHtml(c: ScheduledCall): string {
  const enabled = c.enabled !== false;
  return `
    <div class="auto-row" data-id="${esc(c.id)}" style="padding:14px 16px;border-bottom:1px solid var(--border);display:flex;align-items:center;gap:14px">
      <div style="flex:0 0 auto;min-width:88px">
        <div style="font-size:24px;font-weight:600;color:${enabled ? 'var(--fg)' : 'var(--fg-muted)'};line-height:1.1">${esc(alarmTime(c))}</div>
        <div style="font-size:12px;color:var(--fg-muted);margin-top:2px">${esc(repeatLabel(c))}</div>
      </div>
      <div style="flex:1;min-width:0">
        <div style="font-size:13px;color:var(--fg);overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(c.recipient_phone)}</div>
        <div style="margin-top:3px;display:flex;align-items:center;gap:8px">${statusBadge(c)}${c.last_error ? `<span style="font-size:11px;color:var(--danger,#dc2626)" title="${esc(c.last_error)}">!</span>` : ''}</div>
      </div>
      <button class="auto-toggle" data-id="${esc(c.id)}" data-on="${enabled ? '1' : '0'}" title="${enabled ? 'Turn off' : 'Turn on'}" style="flex:0 0 auto;width:42px;height:24px;border-radius:12px;border:none;cursor:pointer;position:relative;background:${enabled ? 'var(--accent, #16a34a)' : 'var(--bg-elev, #444)'};transition:background .15s">
        <span style="position:absolute;top:2px;left:${enabled ? '20px' : '2px'};width:20px;height:20px;border-radius:50%;background:#fff;transition:left .15s"></span>
      </button>
      <button class="auto-cancel" data-id="${esc(c.id)}" title="Delete" style="flex:0 0 auto;background:none;border:none;cursor:pointer;color:var(--fg-muted);padding:6px;display:inline-flex">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M6 7h12l-1 14H7L6 7zm3-3h6l1 2H8l1-2z"/></svg>
      </button>
    </div>
  `;
}

function segBtn(key: RepeatKind, label: string): string {
  const on = draft.repeat === key;
  return `<button type="button" class="auto-seg" data-rep="${key}" style="flex:1;padding:7px 0;border:1px solid var(--border);background:${on ? 'var(--accent, #16a34a)' : 'transparent'};color:${on ? 'var(--fg-on-accent,#fff)' : 'var(--fg)'};cursor:pointer;font-size:13px;font-weight:600">${label}</button>`;
}

function formHtml(): string {
  const dayChips = DAY_SHORT.map((d, i) => {
    const on = draft.days.has(i);
    return `<button type="button" class="auto-day" data-day="${i}" style="width:34px;height:34px;border-radius:50%;border:1px solid var(--border);background:${on ? 'var(--accent,#16a34a)' : 'transparent'};color:${on ? 'var(--fg-on-accent,#fff)' : 'var(--fg)'};cursor:pointer;font-size:12px;font-weight:600">${d}</button>`;
  }).join('');

  return `
    <div style="padding:16px;border-bottom:1px solid var(--border);background:var(--bg-elev, rgba(127,127,127,0.06))">
      <div style="font-weight:600;color:var(--fg);font-size:14px;margin-bottom:12px">New scheduled call</div>

      <label style="display:block;font-size:12px;color:var(--fg-muted);margin-bottom:4px">Call this number</label>
      <input id="auto-recipient" type="tel" value="${esc(draft.recipient)}" style="width:100%;box-sizing:border-box;padding:8px 10px;border:1px solid var(--border);border-radius:6px;background:var(--bg);color:var(--fg);font-size:13px;margin-bottom:14px;outline:none" />

      <label style="display:block;font-size:12px;color:var(--fg-muted);margin-bottom:4px">Repeat</label>
      <div style="display:flex;border-radius:6px;overflow:hidden;margin-bottom:14px">
        ${segBtn('none', 'Once')}${segBtn('daily', 'Daily')}${segBtn('weekly', 'Weekly')}
      </div>

      <div id="auto-day-row" style="${draft.repeat === 'weekly' ? '' : 'display:none;'}margin-bottom:14px">
        <label style="display:block;font-size:12px;color:var(--fg-muted);margin-bottom:6px">On these days</label>
        <div style="display:flex;gap:6px">${dayChips}</div>
      </div>

      <div style="display:flex;gap:12px;margin-bottom:16px">
        <div id="auto-date-wrap" style="${draft.repeat === 'none' ? 'flex:1' : 'display:none'}">
          <label style="display:block;font-size:12px;color:var(--fg-muted);margin-bottom:4px">Date</label>
          <input id="auto-date" type="date" value="${esc(draft.date)}" style="width:100%;box-sizing:border-box;padding:8px 10px;border:1px solid var(--border);border-radius:6px;background:var(--bg);color:var(--fg);font-size:13px;outline:none" />
        </div>
        <div style="flex:1">
          <label style="display:block;font-size:12px;color:var(--fg-muted);margin-bottom:4px">Time</label>
          <input id="auto-time" type="time" value="${esc(draft.time)}" style="width:100%;box-sizing:border-box;padding:8px 10px;border:1px solid var(--border);border-radius:6px;background:var(--bg);color:var(--fg);font-size:13px;outline:none" />
        </div>
      </div>

      <div style="font-size:12px;color:var(--fg-muted);margin-bottom:14px">Voice flow: asks "Have you left for your appointment?" — you answer out loud. No texts are ever sent.</div>

      <div style="display:flex;gap:8px;justify-content:flex-end">
        <button id="auto-cancel-form" type="button" style="padding:8px 16px;border:1px solid var(--border);border-radius:6px;background:transparent;color:var(--fg);font-size:13px;cursor:pointer">Cancel</button>
        <button id="auto-save" type="button" style="padding:8px 18px;border:none;border-radius:6px;background:var(--accent,#16a34a);color:var(--fg-on-accent,#fff);font-size:13px;font-weight:600;cursor:pointer">Save</button>
      </div>
      <div id="auto-form-error" style="margin-top:10px;font-size:12px;color:var(--danger,#dc2626)"></div>
    </div>
  `;
}

function wireForm(host: HTMLElement): void {
  const body = host.querySelector<HTMLElement>('#auto-body');
  if (!body) return;

  body.querySelectorAll<HTMLButtonElement>('.auto-seg').forEach(btn => {
    btn.addEventListener('click', () => {
      draft.repeat = (btn.dataset['rep'] as RepeatKind) || 'none';
      readForm(body);
      paintBody(host);
    });
  });
  body.querySelectorAll<HTMLButtonElement>('.auto-day').forEach(btn => {
    btn.addEventListener('click', () => {
      const d = Number(btn.dataset['day']);
      if (draft.days.has(d)) draft.days.delete(d); else draft.days.add(d);
      readForm(body);
      paintBody(host);
    });
  });
  body.querySelector<HTMLButtonElement>('#auto-cancel-form')?.addEventListener('click', () => {
    showForm = false;
    paintBody(host);
  });
  body.querySelector<HTMLButtonElement>('#auto-save')?.addEventListener('click', () => { void save(host); });
}

function readForm(body: HTMLElement): void {
  const rec = body.querySelector<HTMLInputElement>('#auto-recipient');
  const time = body.querySelector<HTMLInputElement>('#auto-time');
  const date = body.querySelector<HTMLInputElement>('#auto-date');
  if (rec) draft.recipient = rec.value;
  if (time && time.value) draft.time = time.value;
  if (date && date.value) draft.date = date.value;
}

async function save(host: HTMLElement): Promise<void> {
  const body = host.querySelector<HTMLElement>('#auto-body');
  if (!body) return;
  readForm(body);
  const errEl = body.querySelector<HTMLElement>('#auto-form-error');
  const setErr = (m: string): void => { if (errEl) errEl.textContent = m; };

  const recipient = draft.recipient.trim();
  if (!recipient) { setErr('Enter a number to call.'); return; }
  if (draft.repeat === 'weekly' && draft.days.size === 0) { setErr('Pick at least one day.'); return; }

  const next = computeNext(draft.repeat, draft.time, draft.date, draft.days);
  if (Number.isNaN(next.getTime())) { setErr('Invalid date/time.'); return; }
  if (next.getTime() <= Date.now() + 5000) { setErr('Pick a time in the future.'); return; }

  const saveBtn = body.querySelector<HTMLButtonElement>('#auto-save');
  if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = 'Saving…'; }

  try {
    const flowId = await ensureFlowId();
    const days = Array.from(draft.days).sort((a, b) => a - b);
    const human = draft.repeat === 'none'
      ? `${draft.date} ${draft.time}`
      : draft.repeat === 'daily'
        ? `Daily at ${prettyClock(draft.time)}`
        : `${days.map(d => DAY_LABELS[d]).join(', ')} at ${prettyClock(draft.time)}`;
    await post<ScheduledCall>('/api/scheduled-calls', {
      flow_id: flowId,
      recipient_phone: recipient,
      scheduled_for_utc: next.toISOString(),
      original_time_input: human,
      timezone: browserTz(),
      recurrence_kind: draft.repeat,
      recurrence_days: draft.repeat === 'weekly' ? days : [],
      recurrence_time_local: draft.repeat === 'none' ? '' : draft.time,
      enabled: true,
    });
    showForm = false;
    await loadData();
    paint(host);
  } catch (e) {
    setErr(e instanceof Error ? e.message : 'Could not save. The scheduler backend may not be deployed yet.');
    if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = 'Save'; }
  }
}

function wireList(host: HTMLElement): void {
  const body = host.querySelector<HTMLElement>('#auto-body');
  if (!body) return;
  body.querySelectorAll<HTMLButtonElement>('.auto-toggle').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.dataset['id'];
      const on = btn.dataset['on'] === '1';
      if (id) void toggle(host, id, !on);
    });
  });
  body.querySelectorAll<HTMLButtonElement>('.auto-cancel').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.dataset['id'];
      if (id) void cancel(host, id);
    });
  });
}

async function toggle(host: HTMLElement, id: string, enabled: boolean): Promise<void> {
  try {
    await post<ScheduledCall>(`/api/scheduled-calls/${encodeURIComponent(id)}/toggle`, { enabled });
  } catch {
    /* optimistic — reload regardless */
  }
  await loadData();
  paint(host);
}

async function cancel(host: HTMLElement, id: string): Promise<void> {
  try {
    await post<ScheduledCall>(`/api/scheduled-calls/${encodeURIComponent(id)}/cancel`, {});
  } catch {
    /* ignore */
  }
  await loadData();
  paint(host);
}
